/**
 * applyCommandLive — per-frame preview dispatcher.
 *
 * Called from the viewport on every mouse move while a command buffer is
 * active. Three branches:
 *
 *   - 3D cursor translate (always world-space)
 *   - Edit-mode commands (delegated to commandApplyLiveEdit)
 *   - Pose-mode bone commands (delegated to commandApplyLiveBone)
 *   - Object-mode commands (handled inline here — the only mode whose
 *     three basic ops don't need a deeper split)
 *
 * Shared responsibilities that stay in this file:
 *   - Mouse origin initialization (first call records `mouseOrigin`).
 *   - Numeric-input short-circuit (skip preview when the user has typed).
 *   - Camera/rect fetch + precision scaling (both are per-call state that
 *     the branches uniformly need).
 *   - Allocating a single `ApplyLiveContext` per call and passing it down —
 *     one object allocation per frame, not per command type.
 */

import * as THREE from "three/webgpu";
import { usesTopologyPreview } from "../../modeling/operatorParams.ts";
import { usesIndividualOrigins } from "../../modeling/pivot.ts";
import { applySnapping } from "../../modeling/snap.ts";
import { getInputScale } from "../../viewport/inputScale.ts";
import { selectionToLeafObjectIds } from "../objectSelection.ts";
import { isFullSnapshot } from "../storeUndo.ts";
import { applyLiveBoneCommand } from "./commandApplyLiveBone.ts";
import { applyLiveEditCommand } from "./commandApplyLiveEdit.ts";
import type { ApplyLiveContext, Get_, Set_ } from "./core.ts";
import {
  collectSnapshotTransformTargets,
  computeObjectPivot,
  computeOrientedObjectScale,
  computeRotatedObjectPosition,
  computeScaledObjectPosition,
  computeSnapAlignedObjectRotation,
  visibleHeight,
} from "./transformMath.ts";
import { applyObjectTransformPatch } from "./transformTargets.ts";

export function applyCommandLive(
  set: Set_,
  get: Get_,
  screenX: number,
  screenY: number,
  opts?: { snapGridLevelOffset?: number },
): void {
  const state = get();
  const cb = state.commandBuffer;
  const snap = state.commandPreSnapshot;
  if (!cb || !snap) return;

  // Don't set mouseOrigin during edge loop pick-edge mode — it would
  // anchor the origin at the pick location instead of the drag start
  if (cb.type === "edgeLoopInsert" && !cb.edgeLoopInsertEdge) return;

  // Record initial mouse position on first call
  const effectiveCb = cb.mouseOrigin
    ? cb
    : { ...cb, mouseOrigin: [screenX, screenY] as [number, number] };
  if (!cb.mouseOrigin) {
    set({ commandBuffer: effectiveCb });
    if (!usesTopologyPreview(cb.type)) return;
  }

  // If the user has typed numeric input, skip mouse-driven preview
  if (cb.numericInput) return;

  const camera = window.__poseref_camera;
  const getRect = window.__poseref_canvasRect;
  if (!camera || !getRect) return;

  const rect = getRect();
  const [ox, oy] = effectiveCb.mouseOrigin!;

  // Apply precision scaling — halves pixel deltas in precision mode,
  // uniformly affecting all downstream transforms (translate, rotate, scale).
  const gizmoScale = getInputScale("gizmo", state);
  const dx = (screenX - ox) * gizmoScale;
  const dy = (screenY - oy) * gizmoScale;

  const ctx: ApplyLiveContext = {
    set,
    get,
    state,
    cb: effectiveCb,
    snap,
    camera,
    rect,
    screenX,
    screenY,
    dx,
    dy,
    ox,
    oy,
    snapGridLevelOffset: opts?.snapGridLevelOffset ?? 0,
  };

  // ---------- 3D Cursor: translate only, world-space ----------
  // Intercepts before mode-specific branches — the cursor is always
  // manipulated in world space regardless of the current workspace.
  if (cb.type === "cursorTranslate") {
    applyLiveCursorTranslate(ctx);
    return;
  }

  const inEditMode = state.mode === "edit" && state.editMode != null;
  if (inEditMode) {
    applyLiveEditCommand(ctx);
    return;
  }

  if (cb.type === "boneRotate" || cb.type === "boneTranslate") {
    applyLiveBoneCommand(ctx);
    return;
  }

  // ---------- Object mode: transform selected objects ----------
  applyLiveObjectCommand(ctx);
}

// ---------------------------------------------------------------------------
// 3D cursor translate
// ---------------------------------------------------------------------------

function applyLiveCursorTranslate(ctx: ApplyLiveContext): void {
  const {
    set,
    state,
    cb,
    snap,
    camera,
    rect,
    screenX,
    screenY,
    dx,
    dy,
    snapGridLevelOffset,
  } = ctx;

  // Snapshot cursor position lives on the preSnapshot (we read from
  // state.commandPreSnapshot, but cursor3D is a top-level slice;
  // we instead derive the source from the commandBuffer-time read
  // captured at enterCommand via `cb.mouseOrigin` — the cursor
  // position before the drag is persisted in `snap.cursor3D`).
  const sourcePos = snap.cursor3D?.position ?? state.cursor3D.position;

  const pivotWorld = new THREE.Vector3(
    sourcePos[0],
    sourcePos[1],
    sourcePos[2],
  );
  const dist = camera.position.distanceTo(pivotWorld);
  const vh = visibleHeight(camera, dist);
  const worldPerPx = vh / rect.height;

  const camRight = new THREE.Vector3();
  const camUp = new THREE.Vector3();
  camera.matrixWorld.extractBasis(camRight, camUp, new THREE.Vector3());

  let moveDir: THREE.Vector3;
  if (cb.axis === "normal" && cb.normalVector) {
    const [nvx, nvy, nvz] = cb.normalVector;
    const nv = new THREE.Vector3(nvx, nvy, nvz);
    const axisSS = pivotWorld.clone().add(nv).project(camera);
    const originSS = pivotWorld.clone().project(camera);
    const axisDirSS = new THREE.Vector2(
      axisSS.x - originSS.x,
      axisSS.y - originSS.y,
    );
    if (axisDirSS.length() > 1e-6) axisDirSS.normalize();
    const ndcDx = (dx / rect.width) * 2;
    const ndcDy = -(dy / rect.height) * 2;
    const proj = ndcDx * axisDirSS.x + ndcDy * axisDirSS.y;
    moveDir = nv.multiplyScalar(proj * vh);
  } else if (cb.axis && cb.axis !== "normal") {
    const axisVec = new THREE.Vector3(
      cb.axis === "x" ? 1 : 0,
      cb.axis === "y" ? 1 : 0,
      cb.axis === "z" ? 1 : 0,
    );
    const axisSS = pivotWorld.clone().add(axisVec).project(camera);
    const originSS = pivotWorld.clone().project(camera);
    const axisDirSS = new THREE.Vector2(
      axisSS.x - originSS.x,
      axisSS.y - originSS.y,
    );
    if (axisDirSS.length() > 1e-6) axisDirSS.normalize();
    const ndcDx = (dx / rect.width) * 2;
    const ndcDy = -(dy / rect.height) * 2;
    const proj = ndcDx * axisDirSS.x + ndcDy * axisDirSS.y;
    moveDir = axisVec.multiplyScalar(proj * vh);
  } else {
    moveDir = camRight
      .clone()
      .multiplyScalar(dx * worldPerPx)
      .add(camUp.clone().multiplyScalar(-dy * worldPerPx));
  }

  // Snap the cursor to geometry under the pointer, if snap is enabled.
  // Exclude set is empty — the cursor snaps to everything including
  // the selection it may have come from.
  applySnapping(
    state,
    screenX,
    screenY,
    rect,
    sourcePos,
    moveDir,
    cb.axis,
    cb.normalVector,
    new Set<string>(),
    snapGridLevelOffset,
  );

  const nextPos: [number, number, number] = [
    sourcePos[0] + moveDir.x,
    sourcePos[1] + moveDir.y,
    sourcePos[2] + moveDir.z,
  ];

  set({ cursor3D: { ...state.cursor3D, position: nextPos } });
}

// ---------------------------------------------------------------------------
// Object-mode translate / scale / rotate
// ---------------------------------------------------------------------------

function applyLiveObjectCommand(ctx: ApplyLiveContext): void {
  const {
    set,
    get,
    state,
    cb,
    snap,
    camera,
    rect,
    screenX,
    screenY,
    dx,
    dy,
    ox,
    oy,
    snapGridLevelOffset,
  } = ctx;

  if (!isFullSnapshot(snap)) return;
  const targets = collectSnapshotTransformTargets(
    snap,
    state.selectedObjectTargets,
  );
  const pivot = computeObjectPivot(
    targets,
    state.pivotReference,
    state.cursor3D.position,
  );
  const individualOrigins = usesIndividualOrigins(state.pivotReference);

  // Now compute mouse-derived transform values
  // For translate, we need the camera-plane projection
  // For scale/rotate, we use screen-space ratios/angles

  if (cb.type === "translate") {
    const pivotWorld = new THREE.Vector3(...pivot);
    const dist = camera.position.distanceTo(pivotWorld);
    const vh = visibleHeight(camera, dist);
    const worldPerPx = vh / rect.height;

    const camRight = new THREE.Vector3();
    const camUp = new THREE.Vector3();
    camera.matrixWorld.extractBasis(camRight, camUp, new THREE.Vector3());

    let moveDir: THREE.Vector3;
    if (cb.axis === "normal" && cb.normalVector) {
      // Oriented axis constraint (local/normal/view) — normalVector is
      // a world-space direction. Project mouse delta onto it.
      const [nvx, nvy, nvz] = cb.normalVector;
      const nv = new THREE.Vector3(nvx, nvy, nvz);
      const axisSS = pivotWorld.clone().add(nv).project(camera);
      const originSS = pivotWorld.clone().project(camera);
      const axisDirSS = new THREE.Vector2(
        axisSS.x - originSS.x,
        axisSS.y - originSS.y,
      );
      if (axisDirSS.length() > 1e-6) axisDirSS.normalize();
      const ndcDx = (dx / rect.width) * 2;
      const ndcDy = -(dy / rect.height) * 2;
      const proj = ndcDx * axisDirSS.x + ndcDy * axisDirSS.y;
      const worldDist = proj * vh;
      moveDir = nv.multiplyScalar(worldDist);
    } else if (cb.axis && cb.axis !== "normal") {
      const axisVec = new THREE.Vector3(
        cb.axis === "x" ? 1 : 0,
        cb.axis === "y" ? 1 : 0,
        cb.axis === "z" ? 1 : 0,
      );
      // Project mouse delta onto this world axis (in screen space)
      const axisSS = pivotWorld.clone().add(axisVec).project(camera);
      const originSS = pivotWorld.clone().project(camera);
      const axisDirSS = new THREE.Vector2(
        axisSS.x - originSS.x,
        axisSS.y - originSS.y,
      );
      if (axisDirSS.length() > 1e-6) axisDirSS.normalize();
      const ndcDx = (dx / rect.width) * 2;
      const ndcDy = -(dy / rect.height) * 2;
      const proj = ndcDx * axisDirSS.x + ndcDy * axisDirSS.y;
      const worldDist = proj * vh;
      moveDir = axisVec.multiplyScalar(worldDist);
    } else {
      moveDir = camRight
        .clone()
        .multiplyScalar(dx * worldPerPx)
        .add(camUp.clone().multiplyScalar(-dy * worldPerPx));
    }

    // Snap override: redirect moveDir so the active object lands on the snap target
    const snapResult = applySnapping(
      state,
      screenX,
      screenY,
      rect,
      pivot,
      moveDir,
      cb.axis,
      cb.normalVector,
      selectionToLeafObjectIds(
        state.blendFiles,
        state.collections,
        state.selectedObjectTargets,
      ),
      snapGridLevelOffset,
    );
    const snapNormal =
      state.snapWithRotation && snapResult ? snapResult.worldNormal : null;

    applyObjectTransformPatch(
      set,
      get,
      state,
      targets,
      (target) => {
        const rotationPatch = computeSnapAlignedObjectRotation(
          target,
          snapNormal,
        );
        return {
          position: [
            target.position[0] + moveDir.x,
            target.position[1] + moveDir.y,
            target.position[2] + moveDir.z,
          ],
          ...(rotationPatch ?? {}),
        };
      },
      cb.dependentRebuildTargets,
    );
  } else if (cb.type === "scale") {
    const pivotWorld = new THREE.Vector3(...pivot);
    const pivotSS = pivotWorld.clone().project(camera);
    const pivotPx = new THREE.Vector2(
      (pivotSS.x * 0.5 + 0.5) * rect.width,
      (-pivotSS.y * 0.5 + 0.5) * rect.height,
    );
    const initDist = Math.max(1, Math.hypot(ox - pivotPx.x, oy - pivotPx.y));
    const currDist = Math.max(
      1,
      Math.hypot(screenX - pivotPx.x, screenY - pivotPx.y),
    );
    const factor = currDist / initDist;
    applyObjectTransformPatch(
      set,
      get,
      state,
      targets,
      (target) => {
        const rotationQuat =
          target.kind === "scene"
            ? new THREE.Quaternion().setFromEuler(
                new THREE.Euler(...target.rotationEuler),
              )
            : new THREE.Quaternion(...target.rotationQuat);
        return {
          position: individualOrigins
            ? target.position
            : computeScaledObjectPosition(
                target.position,
                pivot,
                factor,
                cb.axis,
                cb.normalVector,
              ),
          scale: computeOrientedObjectScale(
            target.scale,
            factor,
            cb.axis,
            cb.normalVector,
            rotationQuat,
          ),
        };
      },
      cb.dependentRebuildTargets,
    );
  } else if (cb.type === "rotate") {
    const pivotWorld = new THREE.Vector3(...pivot);
    const pivotSS = pivotWorld.clone().project(camera);
    const pivotPx = new THREE.Vector2(
      (pivotSS.x * 0.5 + 0.5) * rect.width,
      (-pivotSS.y * 0.5 + 0.5) * rect.height,
    );
    const startAngle = Math.atan2(oy - pivotPx.y, ox - pivotPx.x);
    const currAngle = Math.atan2(screenY - pivotPx.y, screenX - pivotPx.x);
    const deltaAngle = currAngle - startAngle;

    // Determine the world-space rotation axis
    let rotAxis: THREE.Vector3;
    if (cb.axis === "normal" && cb.normalVector) {
      rotAxis = new THREE.Vector3(...cb.normalVector).normalize();
    } else if (cb.axis) {
      rotAxis = new THREE.Vector3(
        cb.axis === "x" ? 1 : 0,
        cb.axis === "y" ? 1 : 0,
        cb.axis === "z" ? 1 : 0,
      );
    } else {
      // Default: rotate around view direction
      rotAxis = new THREE.Vector3();
      camera.getWorldDirection(rotAxis);
    }
    const deltaQuat = new THREE.Quaternion().setFromAxisAngle(
      rotAxis,
      deltaAngle,
    );
    const axisTuple: [number, number, number] = [
      rotAxis.x,
      rotAxis.y,
      rotAxis.z,
    ];
    applyObjectTransformPatch(
      set,
      get,
      state,
      targets,
      (target) => {
        if (target.kind === "scene") {
          const snapEuler = new THREE.Euler(...target.rotationEuler);
          const snapQuat = new THREE.Quaternion().setFromEuler(snapEuler);
          const nextQuat = deltaQuat.clone().multiply(snapQuat).normalize();
          const nextEuler = new THREE.Euler().setFromQuaternion(
            nextQuat,
            snapEuler.order,
          );
          return {
            position: individualOrigins
              ? target.position
              : computeRotatedObjectPosition(
                  target.position,
                  pivot,
                  axisTuple,
                  deltaAngle,
                ),
            rotationEuler: [nextEuler.x, nextEuler.y, nextEuler.z],
          };
        }

        const snapQuat = new THREE.Quaternion(...target.rotationQuat);
        const nextQuat = deltaQuat.clone().multiply(snapQuat).normalize();
        return {
          position: individualOrigins
            ? target.position
            : computeRotatedObjectPosition(
                target.position,
                pivot,
                axisTuple,
                deltaAngle,
              ),
          rotationQuat: [nextQuat.x, nextQuat.y, nextQuat.z, nextQuat.w],
        };
      },
      cb.dependentRebuildTargets,
    );
  }
}
